import { Data, Layout } from "plotly.js";
import {
  getDefaultPlotData,
  getLegendConfig,
  getPlotLayout,
} from "../plotConfig";

import { stbColors } from "Components/Themes/Storebrand/storebrandColors";
import { formatToLocaleString } from "Formatting/formatNumber";
import { WindowSize } from "Utils/screenSizeUtils";
import { AdvicePlotDisabilityProps } from "./AdvicePlotDisability";

export const getLayout = (
  props: AdvicePlotDisabilityProps,
  showOtherPayout: boolean,
  mobileVariant: boolean,
  windowSize: WindowSize,
): Partial<Layout> => {
  const {
    monthlyIncome,
    monthlyNetNavPayout,
    monthlyNetEmployerPaidDisabilityPension,
    monthlyPaymentWithDisabilityInsurance,
  } = props;

  const ticktext = showOtherPayout ? ["", "", "", ""] : ["", "", ""];
  return {
    ...getPlotLayout(
      {
        tickvals: showOtherPayout
          ? [
              monthlyNetNavPayout,
              monthlyNetNavPayout + monthlyNetEmployerPaidDisabilityPension,
              monthlyPaymentWithDisabilityInsurance,
              monthlyIncome,
            ]
          : [
              monthlyNetNavPayout,
              monthlyPaymentWithDisabilityInsurance,
              monthlyIncome,
            ],
        ticktext,
      },
      windowSize,
      {
        ...(mobileVariant && {
          height: showOtherPayout ? 300 : 250,
          marginR: 0,
        }),
        ...(!mobileVariant && showOtherPayout && { width: 475, marginR: 130 }),
      },
    ),
    ...(mobileVariant && getLegendConfig(-0.9)),
  };
};

export const getData = (
  props: AdvicePlotDisabilityProps,
  showOtherPayout: boolean,
  mobileVariant: boolean,
  // eslint-disable-next-line sonarjs/cognitive-complexity
): Data[] => {
  const {
    monthlyIncome,
    monthlyNetNavPayout,
    monthlyNetEmployerPaidDisabilityPension,
    monthlyPaymentWithDisabilityInsurance,
    valueType,
    advicePlot,
    lang,
  } = props;

  const getStringOf = (value: number): string =>
    `${formatToLocaleString(value)} ${valueType}`;
  const xValues: string[] = advicePlot.xValues[lang];
  const xValuesMobile: string[] = advicePlot.xValuesMobile[lang];

  const dataLayer1 = {
    ...getDefaultPlotData(),
    x: mobileVariant ? xValuesMobile : xValues,
    y: mobileVariant ? [monthlyIncome, 0] : [monthlyIncome, 0, 0],
    text: mobileVariant
      ? [getStringOf(monthlyIncome), ""]
      : [getStringOf(monthlyIncome), "", ""],
    marker: { color: stbColors.charts.blue70 },
    showlegend: false,
  };
  const dataLayer2 = {
    ...getDefaultPlotData(),
    x: mobileVariant ? xValuesMobile : xValues,
    y: mobileVariant
      ? [0, monthlyNetNavPayout]
      : [0, monthlyNetNavPayout, monthlyNetNavPayout],
    text: mobileVariant
      ? ["", ""]
      : ["", showOtherPayout ? "" : getStringOf(monthlyNetNavPayout), ""],
    marker: { color: stbColors.charts.blue30 },
    name: advicePlot.fromNav[lang],
  };
  const dataLayer3 = {
    ...getDefaultPlotData(),
    x: mobileVariant ? xValuesMobile : xValues,
    y: mobileVariant
      ? [0, monthlyNetEmployerPaidDisabilityPension]
      : [
          0,
          monthlyNetEmployerPaidDisabilityPension,
          monthlyNetEmployerPaidDisabilityPension,
        ],
    text: mobileVariant
      ? ["", ""]
      : [
          "",
          getStringOf(
            monthlyNetNavPayout + monthlyNetEmployerPaidDisabilityPension,
          ),
          "",
        ],
    marker: { color: stbColors.charts.blue50 },
    name: advicePlot.fromEmployer[lang],
  };

  const monthlytWithInsurance =
    monthlyPaymentWithDisabilityInsurance -
    monthlyNetNavPayout -
    (showOtherPayout ? monthlyNetEmployerPaidDisabilityPension : 0);
  const dataLayer4 = {
    ...getDefaultPlotData(),
    x: mobileVariant ? xValuesMobile : xValues,
    y: mobileVariant
      ? [0, monthlytWithInsurance]
      : [0, 0, monthlytWithInsurance],
    text: mobileVariant
      ? ["", getStringOf(monthlyPaymentWithDisabilityInsurance)]
      : ["", "", getStringOf(monthlyPaymentWithDisabilityInsurance)],
    marker: { color: stbColors.charts.brand },
    name: advicePlot.withInsurance[lang],
  };

  return showOtherPayout
    ? [dataLayer1, dataLayer2, dataLayer3, dataLayer4]
    : [dataLayer1, dataLayer2, dataLayer4];
};
